import { type GreetingModel } from '@/shared/api'
import { TABLE_PAGE_SIZE_OPTIONS } from '@/shared/lib/query'
import { TableWrapper } from '@/shared/ui'
import { Table, type TableProps } from 'antd'
import { useUnit } from 'effector-react'

import { type GreetingsSortState } from '../../lib/greetings-query-model'
import {
  $greetingsTablePage,
  $greetingsTablePageSize,
  $greetingsTableSort,
  greetingIdsSelected,
  greetingsTablePageChanged,
  greetingsTablePageReset,
  greetingsTablePageSizeChanged,
  greetingsTableQuery,
  greetingsTableSortChanged,
  greetingsTableSortReset,
} from '../../model/greetings-table.model'
import { useGreetingsTableColumns } from './useGreetingsTableColumns'

interface GreetingsTableProps {
  onEdit: (greetingId: string) => void
}
export const GreetingsTable = ({ onEdit }: GreetingsTableProps) => {
  const { data, pending } = useUnit(greetingsTableQuery)

  const sort = useUnit($greetingsTableSort)
  const changeSort = useUnit(greetingsTableSortChanged)
  const resetSort = useUnit(greetingsTableSortReset)

  const page = useUnit($greetingsTablePage)
  const changePage = useUnit(greetingsTablePageChanged)
  const resetPage = useUnit(greetingsTablePageReset)
  const pageSize = useUnit($greetingsTablePageSize)
  const changePageSize = useUnit(greetingsTablePageSizeChanged)

  const changeSelectedIds = useUnit(greetingIdsSelected)

  const tableColumns: TableProps<GreetingModel>['columns'] =
    useGreetingsTableColumns({ onEdit, sort })

  if (!data?.items || !data.items.length) {
    return null
  }

  const onTableChange: TableProps<GreetingModel>['onChange'] = (
    pagination,
    _filters,
    sorter,
  ) => {
    // Sort
    if (!Array.isArray(sorter)) {
      const field = sorter.field as string | undefined
      const order = sorter.order as 'ascend' | 'descend' | undefined

      if (field && order) {
        const sortOrder = order === 'ascend' ? 'asc' : 'desc'

        if (sort.field !== field || sort.order !== sortOrder) {
          changeSort({
            field: field as GreetingsSortState['field'],
            order: sortOrder,
          })
          resetPage()
        }
      } else if (sort.field !== null || sort.order !== null) {
        resetSort()
        resetPage()
      }
    }
    // Pagination
    if (pagination.current && pagination.pageSize) {
      if (pagination.current !== page) {
        changePage(pagination.current)
      }
      if (pagination.pageSize !== pageSize) {
        changePageSize(pagination.pageSize)
        resetPage()
      }
    }
  }

  // Selection

  const rowSelection: TableProps<GreetingModel>['rowSelection'] = {
    onChange: (_, selectedRows: GreetingModel[]) => {
      const ids = selectedRows.map((row) => row.id)
      changeSelectedIds(ids)
    },
    getCheckboxProps: (record: GreetingModel) => ({
      name: record.name,
    }),
  }

  return (
    <TableWrapper>
      <Table
        sticky
        showSorterTooltip={false}
        rowKey={(record) => record.id}
        bordered
        rowSelection={{ type: 'checkbox', ...rowSelection }}
        columns={tableColumns}
        dataSource={data.items}
        loading={pending}
        onChange={onTableChange}
        pagination={{
          current: page,
          pageSize: pageSize,
          total: data.totalItems,
          showSizeChanger: true,
          pageSizeOptions: TABLE_PAGE_SIZE_OPTIONS,
          locale: { items_per_page: 'Шаблонов на странице' },
        }}
      />
    </TableWrapper>
  )
}
